import React, { useEffect, useState } from "react";
import * as Actions from "../../../redux/actions/";
import {
  Box, Typography, Container, Button, Snackbar,
  Alert, CircularProgress, Backdrop, Chip, TextField, Grid,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import DropFileInput from "../../../components/dropFileInput/DropFileInput";
import SelectedMediaView from "../../../components/seller/listings/SelectedMediaView";
import axios from "axios";
import config from "../../../config";

import BadgeIcon from "@mui/icons-material/Badge";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.04)",
    color: "#fff",
    borderRadius: "10px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "#FFC001" },
    "&.Mui-focused fieldset": { borderColor: "#FFC001", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.25)", opacity: 1 },
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiFormHelperText-root": { color: "#555" },
};

const SectionLabel = ({ icon, title, description }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 1.5 }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: "9px", flexShrink: 0,
      backgroundColor: "rgba(255,192,1,0.08)", border: "1px solid rgba(255,192,1,0.15)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {React.cloneElement(icon, { sx: { color: "#FFC001", fontSize: 17 } })}
    </Box>
    <Box>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>{title}</Typography>
      {description && <Typography sx={{ color: "#555", fontSize: "12px", mt: 0.2 }}>{description}</Typography>}
    </Box>
  </Box>
);

const SellerDocuments = ({ user, jwtToken, seller, setSeller, setUserIsSubscribed, refreshSeller }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramValue = queryParams.get("success");

  const [previousSellerNumber, setPreviousSellerNumber] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [media, setMedia] = useState({ idMedia: [], sellerMedia: [], vetMedia: [] });

  useEffect(() => {
    if (user && user.is_subscribed) refreshSeller();
  }, []);

  useEffect(() => {
    if (user && user.is_subscribed && seller && seller.approval_status === "approved") {
      return navigate("/");
    }
    if (user && !user.is_subscribed) {
      return navigate("/pricing");
    }
  }, [user, seller]);

  useEffect(() => {
    if (paramValue === "true") setUserIsSubscribed();
  }, [paramValue]);

  const onIdMediaChange = (file) => {
    if (file.size > 0) setMedia(m => ({ ...m, idMedia: [...m.idMedia, file] }));
  };

  const onSellerMediaChange = (file) => {
    const MAX = 50 * 1024 * 1024;
    if (file.size > MAX) {
      setSnackbar({ open: true, message: `"${file.name}" exceeds the 50 MB limit.`, severity: "error" });
      return;
    }
    if (file.size > 0) setMedia(m => ({ ...m, sellerMedia: [...m.sellerMedia, file] }));
  };

  const onVetMediaChange = (file) => {
    if (file.size > 0) setMedia(m => ({ ...m, vetMedia: [...m.vetMedia, file] }));
  };

  const onIdMediaRemove = (file) => setMedia(m => ({ ...m, idMedia: m.idMedia.filter(f => f !== file) }));
  const onSellerMediaRemove = (file) => setMedia(m => ({ ...m, sellerMedia: m.sellerMedia.filter(f => f !== file) }));
  const onVetMediaRemove = (file) => setMedia(m => ({ ...m, vetMedia: m.vetMedia.filter(f => f !== file) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    const fields = ["previous_seller_number", "address", "references"];
    const formValues = fields.reduce((acc, f) => ({ ...acc, [f]: e.target.elements.namedItem(f).value }), {});

    if (media.idMedia.length > 0 && media.sellerMedia.length > 0 && media.vetMedia.length > 0) {
      let req;
      try {
        req = await axios.post(
          `${config.SERVER_URL}/api/v1/seller`,
          {
            seller: {
              id_media: media.idMedia,
              seller_media: media.sellerMedia,
              contact: formValues.previous_seller_number,
              address: formValues.address,
              reference: formValues.references,
              vet_media: media.vetMedia,
              user_id: user.id,
            },
          },
          { headers: { "Content-Type": "multipart/form-data; ", Authorization: `Bearer ${jwtToken}` } }
        );
      } catch ({ response }) {
        req = response;
        if (req.status === 422) {
          setSnackbar({ open: true, message: Object.keys(req.data.errors) + ": " + Object.values(req.data.errors), severity: "error" });
          setProcessing(false);
        }
      }
      if (req?.status === 201) {
        setSeller(req.data.data);
        setSnackbar({ open: true, message: req.data.message, severity: "success" });
        setProcessing(false);
      }
    } else {
      setSnackbar({ open: true, message: "Please upload all required documents.", severity: "error" });
      setProcessing(false);
    }
  };

  const onPhoneNumberChange = (e) => {
    const pattern = /^[A-Za-z!@#$%^&*()_+`~=[\\\]/.,{}|:;"'<>?*]+$/;
    e.preventDefault();
    const val = e.target.value;
    if (val.length === 3 && e.nativeEvent.inputType !== "deleteContentBackward") {
      setPreviousSellerNumber(val + "-");
    } else if (val.length === 4 && e.nativeEvent.inputType === "insertText") {
      setPreviousSellerNumber(previousSellerNumber + "-" + val[val.length - 1]);
    } else if (val.length === 7 && e.nativeEvent.inputType !== "deleteContentBackward") {
      setPreviousSellerNumber(val + "-");
    } else if (val.length === 8 && e.nativeEvent.inputType === "insertText") {
      setPreviousSellerNumber(previousSellerNumber + "-" + val[val.length - 1]);
    } else {
      if (!pattern.test(val)) setPreviousSellerNumber(val);
    }
  };

  // ── Pending state ──
  if (seller && seller.approval_status === "pending") {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#111", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center", maxWidth: 480, px: 3 }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: "50%", mx: "auto", mb: 3,
            backgroundColor: "rgba(255,192,1,0.1)", border: "2px solid rgba(255,192,1,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <HourglassTopIcon sx={{ color: "#FFC001", fontSize: 36 }} />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: "28px", color: "#fff", mb: 1.5 }}>
            Application Under Review
          </Typography>
          <Typography sx={{ color: "#666", fontSize: "15px", lineHeight: 1.8, mb: 4 }}>
            Your seller application has been submitted and is currently being reviewed by our team. We'll notify you once you're approved.
          </Typography>
          <Button
            component={Link} to="/"
            sx={{
              px: 4, py: 1, borderRadius: "10px", backgroundColor: "#FFC001",
              color: "#000", fontWeight: 800, fontSize: "14px", textTransform: "none",
              "&:hover": { backgroundColor: "#e6ac00" },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={processing}>
        <CircularProgress sx={{ color: "#FFC001" }} />
      </Backdrop>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled" severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Hero ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 5, md: 7 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Seller Registration</Typography>
            </Box>
            <Chip label="BECOME A SELLER" size="small" sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }} />
            <Typography sx={{ fontWeight: 900, fontSize: { xs: "28px", md: "44px" }, color: "#fff", lineHeight: 1.1, mb: 1.5 }}>
              Seller <Box component="span" sx={{ color: "#FFC001" }}>Documents</Box>
            </Typography>
            <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" }, maxWidth: 520 }}>
              All documents are kept strictly confidential and used only for verification purposes.
            </Typography>
          </Container>
        </Box>

        {/* ── Form ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

                {/* ID Picture */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<BadgeIcon />} title="Your ID Picture" description="A clear photo of a government-issued ID (passport, driver's license, etc.)" />
                  <DropFileInput onFileChange={onIdMediaChange} filesUrls={media.idMedia} disabled={false} acceptFileTypes=".jpg, .jpeg, .gif, .png, .webp, .heic" />
                  {media.idMedia.length > 0 && <SelectedMediaView media={media.idMedia} onFileRemove={onIdMediaRemove} />}
                </Box>

                {/* Pics & Videos */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<PhotoLibraryIcon />} title="Pics & Videos of Your Animals" description="Photos or videos showcasing your animals. Max file size: 50 MB." />
                  <DropFileInput onFileChange={onSellerMediaChange} filesUrls={media.sellerMedia} disabled={false} acceptFileTypes=".jpg, .jpeg, .gif, .png, .webp, .heic, .mp4, .mkv, .mov" />
                  {media.sellerMedia.length > 0 && <SelectedMediaView media={media.sellerMedia} onFileRemove={onSellerMediaRemove} />}
                </Box>

                {/* Contact Info */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<PhoneIcon />} title="Previous Seller Number" description="Your contact number as a previous seller, if applicable." />
                  <TextField
                    placeholder="415-555-2671"
                    helperText="Format: 415-555-2671"
                    size="small"
                    type="tel"
                    value={previousSellerNumber}
                    name="previous_seller_number"
                    fullWidth
                    autoComplete="off"
                    onChange={onPhoneNumberChange}
                    inputProps={{ minLength: 12, maxLength: 12, pattern: "[0-9]{3}-[0-9]{3}-[0-9]{4}" }}
                    required
                    sx={inputSx}
                  />
                </Box>

                {/* Address */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<HomeIcon />} title="Previous Seller Address" description="Your business or home address as a previous seller." />
                  <TextField placeholder="Enter your address" size="small" type="text" name="address" fullWidth required sx={inputSx} />
                </Box>

                {/* Vet References */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<LocalHospitalIcon />} title="Vet References" description="Phone number or address of a veterinarian who can vouch for your animals." />
                  <TextField placeholder="Vet name, phone number, or address" size="small" type="text" name="references" fullWidth required sx={inputSx} />
                </Box>

                {/* Vet Paperwork */}
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <SectionLabel icon={<DescriptionIcon />} title="Vet Paperwork" description="Health certificates, vaccination records, or any other vet documentation." />
                  <DropFileInput onFileChange={onVetMediaChange} filesUrls={media.vetMedia} disabled={false} acceptFileTypes=".jpg, .jpeg, .gif, .png, .webp, .heic, .pdf, .docx" />
                  {media.vetMedia.length > 0 && <SelectedMediaView media={media.vetMedia} onFileRemove={onVetMediaRemove} />}
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
                  <Button
                    onClick={() => navigate(-1)}
                    sx={{
                      px: 3, py: 1, borderRadius: "10px", border: "1px solid #333",
                      color: "#666", fontWeight: 700, fontSize: "14px", textTransform: "none",
                      "&:hover": { borderColor: "#555", color: "#ccc", backgroundColor: "rgba(255,255,255,0.03)" },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={processing}
                    sx={{
                      px: 4, py: 1, borderRadius: "10px",
                      backgroundColor: "#FFC001", color: "#000",
                      fontWeight: 800, fontSize: "14px", textTransform: "none",
                      "&:hover": { backgroundColor: "#e6ac00" },
                      "&.Mui-disabled": { backgroundColor: "#333", color: "#666" },
                    }}
                  >
                    {processing ? <CircularProgress size={20} sx={{ color: "#FFC001" }} /> : "Submit Application"}
                  </Button>
                </Box>

              </Box>
            </Grid>

            {/* ── Right sidebar: info ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: "sticky" }, top: 24 }}>
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3, mb: 2 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#fff", mb: 2 }}>What happens next?</Typography>
                  {[
                    { step: "1", text: "Submit your documents" },
                    { step: "2", text: "Our team reviews your application (1–3 business days)" },
                    { step: "3", text: "You're approved and can start listing animals" },
                  ].map(({ step, text }) => (
                    <Box key={step} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: 0.1,
                        backgroundColor: "rgba(255,192,1,0.1)", border: "1px solid rgba(255,192,1,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Typography sx={{ color: "#FFC001", fontSize: "11px", fontWeight: 800 }}>{step}</Typography>
                      </Box>
                      <Typography sx={{ color: "#888", fontSize: "13px", lineHeight: 1.6 }}>{text}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#FFC001", fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#fff" }}>All documents are confidential</Typography>
                  </Box>
                  <Typography sx={{ color: "#666", fontSize: "13px", lineHeight: 1.7 }}>
                    Your documents are encrypted and only accessible to our verification team. They will never be shared with third parties.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = ({ Auth, Seller }) => ({
  user: Auth.user,
  jwtToken: Auth.jwtToken,
  seller: Seller.seller,
});

const mapDispatchToProps = (dispatch) => ({
  setSeller: (data) => dispatch(Actions.setSeller(data)),
  setUserIsSubscribed: () => dispatch(Actions.setUserIsSubscribed()),
  refreshSeller: () => dispatch(Actions.thunkGetSeller()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SellerDocuments);
